import { Terminal } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace, useIdeSessionsPage } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { IdeSessionCard } from "~/features/console/IdeSessionCard";
import { orderSessions } from "~/features/console/ideSession";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";

/**
 * `/console/ides` — the windows themselves.
 *
 * A route rather than a settings section, and the promotion is deliberate: settings is where
 * you go to CHANGE something, and there was nowhere to go to WATCH something. Everything on
 * this page is an observation; the only two verbs are aimed at a single window.
 *
 * There is no ability gate, and that is correct (§E-14): every member sees their OWN windows,
 * and only an Admin sees the workspace's. Gating the page on `canAdminister` would hide a
 * person's own machines from them.
 */
export function IdeConsoleScene() {
  const { t } = useTranslation();
  const { data: page, isLoading } = useIdeSessionsPage();
  const { data: workspace } = useCurrentWorkspace();
  const canRevokeAny = workspace?.abilities[ABILITY.workspaceUpdate] === true;

  const sessions = orderSessions(page?.sessions ?? []);

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-6 py-8">
      <header className="flex flex-col gap-1">
        <h1 className="text-h1 font-semibold text-text">{t("IDE Console")}</h1>
        <p className="text-body text-text-secondary">
          {t(
            "Your VS Code windows running the Docit extension — what each one owns, whether it is online, and whether it accepts delegated work. Windows heartbeat every 30 seconds; silence beyond 90 seconds shows as offline.",
          )}
        </p>
      </header>

      {page?.admin ? (
        <p className="text-small text-text-tertiary">
          {t("You are an admin, so this lists everybody's windows — not only your own.")}
        </p>
      ) : null}

      {isLoading ? <Spinner label={t("Loading")} /> : null}

      {!isLoading && sessions.length === 0 ? (
        <Empty
          icon={Terminal}
          message={t("No connected IDEs yet. Open VS Code with the Docit extension and sign in.")}
        />
      ) : null}

      {sessions.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {sessions.map((session) => (
            <IdeSessionCard
              key={session.sessionKey}
              session={session}
              advisedVersion={page?.minimumAdvisedExtensionVersion ?? null}
              showOwner={page?.admin === true}
              // The server enforces owner-or-Admin; the client says the same thing so a
              // button that would be refused is never offered.
              canRevoke={session.isOwn || canRevokeAny === true}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
